import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, DollarSign, TrendingUp, Package, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCoordinatorProcurements, useCoordinatorProcurementStats } from '@/hooks/use-coordinator-procurements';

const CoordinatorProcurementSection: React.FC = () => {
  const { data: procurements = [], isLoading } = useCoordinatorProcurements();
  const { data: stats } = useCoordinatorProcurementStats();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'finance_approved': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'warehouse_confirmed': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'initiated': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'finance_rejected': 
      case 'warehouse_rejected': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatStatus = (status: string) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Procurement Management</h2>
          <p className="text-muted-foreground">Track purchases linked to your farmer registrations</p>
        </div>
        <Button asChild>
          <Link to="/procurement-form">
            <ShoppingCart className="h-4 w-4 mr-2" />
            Create Procurement
          </Link>
        </Button>
      </div>

      {/* Procurement Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Package className="h-5 w-5 text-blue-600" />
              <div>
                <div className="text-2xl font-bold">{stats?.totalProcurements || 0}</div>
                <p className="text-sm text-muted-foreground">Total Procurements</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              <div>
                <div className="text-2xl font-bold">
                  {stats?.totalValue ? `$${stats.totalValue.toLocaleString()}` : '$0'}
                </div>
                <p className="text-sm text-muted-foreground">Total Value</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-purple-600" />
              <div>
                <div className="text-2xl font-bold">{stats?.activeProcurements || 0}</div>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Package className="h-5 w-5 text-orange-600" />
              <div>
                <div className="text-2xl font-bold">{stats?.recentProcurements || 0}</div>
                <p className="text-sm text-muted-foreground">Last 30 Days</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Procurements */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Procurements</CardTitle>
          <CardDescription>Latest procurement activities from your farmers</CardDescription>
        </CardHeader>
        <CardContent>
          {procurements.length > 0 ? (
            <div className="space-y-4">
              {procurements.slice(0, 5).map((procurement) => (
                <div key={procurement.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div>
                        <p className="font-medium">{procurement.product_name || 'Unknown Product'}</p>
                        <p className="text-sm text-muted-foreground">
                          {procurement.farmer?.farmer_name} • {procurement.farmer?.region}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="text-right">
                      <p className="font-medium">
                        {procurement.total_quantity ? `${procurement.total_quantity}kg` : 'N/A'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {procurement.total_amount ? `$${procurement.total_amount.toLocaleString()}` : 'N/A'}
                      </p>
                    </div>
                    
                    <Badge className={getStatusColor(procurement.status)}>
                      {formatStatus(procurement.status)}
                    </Badge>
                    
                    <Button asChild variant="ghost" size="sm">
                      <Link to={`/procurement/${procurement.id}`}>
                        <Eye className="h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              ))}
              
              {procurements.length > 5 && (
                <div className="text-center pt-4">
                  <Button asChild variant="outline">
                    <Link to="/procurements">
                      View All Procurements ({procurements.length})
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No procurements found</p>
              <Button asChild>
                <Link to="/procurement-form">
                  Create First Procurement
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CoordinatorProcurementSection;